import { format, isValid } from 'date-fns'
import { motion } from 'framer-motion'
import React, { useContext, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useStore } from 'zustand'

import {
  AuthenticatedGeneralConsultationRequest,
  Gender,
  GeneralConsultationRequest,
  IHIdentifier,
  PrescriptionRequest,
} from '@/lib/apis/types'
import { useStore as useGlobalStore } from '@/lib/store'
import {
  NotificationItem,
  NotificationItemVariant,
} from '@/lib/models/NotificationItem'
import { translateError } from '@/lib/apis/error'
import { FreeTextPrescriptionContentKey } from '@/features/prescriptions/PrescriptionConstants'
import useApiClient from '@/lib/apis/clients/useApiClient'
import { CreateGeneralConsultationRequest } from '@/lib/apis/general-consultations'
import {
  type ConsultationQuestionnaireType,
  generalConsultationFormToDto,
} from '@/lib/questionnaires/GeneralConsultation'
import {
  PrescriptionQuizFooter,
  prescriptionQuestionItemMotion,
  prescriptionQuestionListMotion,
} from '@/features/prescriptions/request/PrescriptionQuizShell'
import {
  QuizReviewCard,
  QuizReviewSection,
  QuizReviewSections,
  type QuizReviewRow,
} from '@/shared/components/quiz/QuizReviewSection'
import { GENDERS } from '@/lib/types'
import { parseDate } from '@/lib/utils'
import { ConsultationRequestFormContext } from './ConsultationRequestFormProvider'

type CommunicationTokens = Pick<PrescriptionRequest, 'tokens'>

export function ConsultationRequestReviewStage() {
  const store = useContext(ConsultationRequestFormContext)
  if (!store)
    throw new Error(
      'ConsultationRequestReviewStage must be used within a ConsultationRequestFormContextProvider',
    )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const showNotification = useGlobalStore((state) => state.showNotification)
  const onPrev = useStore(store, (state) => state.onPrev)
  const session = useStore(store, (state) => state.session)
  const goToStage = useStore(store, (state) => state.goToStage)
  const questionnaireForm = useStore(store, (state) => state.questionnaireForm)
  const profileFormState = useStore(store, (state) => state.profileFormState)
  const ihiVerificationState = useStore(
    store,
    (state) => state.ihiVerificationState,
  )
  const supportingDocuments = useStore(
    store,
    (state) => state.supportingDocuments,
  )
  const selectedSchedule = useStore(store, (state) => state.selectedSchedule)
  const showPersonalDetails = useStore(store, (state) =>
    state.stages.some((stage) => stage.type === 'PERSONAL-INFORMATION'),
  )

  const api = useApiClient()
  const generalConsultationMutation = useMutation(
    CreateGeneralConsultationRequest.mutation(api),
  )

  async function submitRequest() {
    setIsSubmitting(true)
    try {
      const data = {
        metadata: {
          type: 'legacy',
          questionnaire: generalConsultationFormToDto(questionnaireForm.values),
        },
        allowsAutoNoteTakingSoftware:
          questionnaireForm.values.allowsAutoNoteTakingSoftware === 'Yes',
        documents: supportingDocuments.map((document) => {
          if (document.uploadState.uploading)
            throw new Error('Invalid upload state for the documents')
          return {
            token: document.uploadState.token,
            description: document.description,
          }
        }),
        ...(!session
          ? {
              profile: {
                type: 'patient' as const,
                ...profileFormState.values,
                gender: profileFormState.values.gender as Gender,
              },
              tokens: computeCommunicationTokens().tokens,
              ihIdentifier: computeIHIdentifierValue(),
            }
          : {}),
        scheduledSlot:
          selectedSchedule?.type === 'scheduled'
            ? { instantMillis: selectedSchedule.selected.millis }
            : null,
      } as GeneralConsultationRequest | AuthenticatedGeneralConsultationRequest

      const checkoutResult = await generalConsultationMutation.mutateAsync(data)
      localStorage.removeItem(FreeTextPrescriptionContentKey)
      window.location.assign(checkoutResult.checkoutUrl)
    } catch (error) {
      console.error(error)
      showNotification(
        new NotificationItem(
          translateError(error),
          NotificationItemVariant.Error,
        ),
      )
      setIsSubmitting(false)
    }
  }

  function computeIHIdentifierValue(): IHIdentifier | null {
    if (!ihiVerificationState.verified) return null
    if (ihiVerificationState.id === 'MED')
      return {
        type: 'med',
        number: ihiVerificationState.verified.number,
        irn: ihiVerificationState.verified.irn!,
        expiry: ihiVerificationState.verified.expiry,
      }
    if (ihiVerificationState.id === 'IHI')
      return { type: 'ihi', ...ihiVerificationState.verified }
    return null
  }

  function computeCommunicationTokens(): CommunicationTokens {
    const result =
      profileFormState.verification.mobileVerificationResult?.alreadyVerified
    return {
      tokens: {
        email: null,
        mobile:
          result === false
            ? (profileFormState.verification.mobileVerificationResult?.token ??
              null)
            : null,
      },
    }
  }

  const detailRows: QuizReviewRow[] = [
    {
      label: 'Name',
      value: `${profileFormState.values.firstName} ${profileFormState.values.lastName}`,
    },
    {
      label: 'Gender',
      value:
        GENDERS[profileFormState.values.gender as keyof typeof GENDERS] ??
        profileFormState.values.gender,
    },
    { label: 'Email', value: profileFormState.values.email },
    { label: 'Mobile', value: profileFormState.values.mobile },
    {
      label: 'Date of birth',
      value: displayReviewDate(profileFormState.values.dob),
    },
    {
      label: 'Address',
      value: formatReviewAddress(profileFormState.values),
    },
  ]

  return (
    <motion.div
      animate="animate"
      className="flex flex-col"
      initial="initial"
      variants={prescriptionQuestionListMotion}
    >
      <QuizReviewSections>
        <motion.div variants={prescriptionQuestionItemMotion}>
          <QuizReviewSection
            onChange={() => goToStage('QUESTIONNAIRE', 0)}
            rows={buildAnswerRows(questionnaireForm.values)}
            title="Your consultation"
          />
        </motion.div>

        <motion.div variants={prescriptionQuestionItemMotion}>
          <QuizReviewSection
            last={supportingDocuments.length === 0 && !showPersonalDetails}
            onChange={() => goToStage('CALL-ARRANGEMENT')}
            rows={[
              {
                label: 'Doctor callback',
                value:
                  selectedSchedule?.type === 'scheduled'
                    ? `${selectedSchedule.selected.day}, ${selectedSchedule.selected.date} at ${selectedSchedule.selected.time}`
                    : 'As soon as possible — within 2 hours',
              },
            ]}
            title="Call time"
          />
        </motion.div>

        {supportingDocuments.length > 0 && (
          <motion.div variants={prescriptionQuestionItemMotion}>
            <QuizReviewSection
              last={!showPersonalDetails}
              onChange={() => goToStage('UPLOAD-DOCUMENTS')}
              title="Your documents"
            >
              <QuizReviewCard className="py-3.5">
                {supportingDocuments.map((document) => (
                  <div
                    className="flex items-center justify-between gap-4 py-1 text-[14px] leading-5"
                    key={document.name}
                  >
                    <span className="truncate font-medium">
                      {document.name}
                    </span>
                    <span className="shrink-0 text-[12.5px] text-[#82828B]">
                      Uploaded
                    </span>
                  </div>
                ))}
              </QuizReviewCard>
            </QuizReviewSection>
          </motion.div>
        )}

        {showPersonalDetails && (
          <motion.div variants={prescriptionQuestionItemMotion}>
            <QuizReviewSection
              last
              onChange={() => goToStage('PERSONAL-INFORMATION')}
              rows={detailRows}
              title="Your details"
            />
          </motion.div>
        )}
      </QuizReviewSections>

      <motion.div variants={prescriptionQuestionItemMotion}>
        <PrescriptionQuizFooter
          backDisabled={isSubmitting}
          nextLabel="Continue to payment"
          nextLoading={isSubmitting}
          note="You’re only charged once the doctor accepts your request."
          onBack={onPrev}
          onNext={() => void submitRequest()}
        />
      </motion.div>
    </motion.div>
  )
}

function buildAnswerRows(
  values: ConsultationQuestionnaireType,
): QuizReviewRow[] {
  const entries: Array<[string, unknown]> = [
    ['What you want to discuss', values.description],
    ['For yourself', values.isForSelf],
    ['18 or older', values.isOlderThan18],
    ['Significant conditions', values.hasSignificantMedicalConditions],
    ['Condition details', values.significantMedicalConditions],
    ['Regular medicines', values.isTakingRegularMedications],
    ['Medicine details', values.regularMedications],
    ['Allergies', values.hasAllergies],
    ['Allergy details', values.allergies],
    ['In Australia', values.isInAustralia],
    ['Secure note taking', values.allowsAutoNoteTakingSoftware],
  ]

  return entries
    .filter(([, value]) => String(value ?? '').trim().length > 0)
    .map(([label, value]) => ({
      label,
      value: String(value),
      fullWidth: label === 'What you want to discuss',
    }))
}

function displayReviewDate(value?: string | null) {
  if (!value) return ''
  const parsed = parseDate(value)
  return isValid(parsed) ? format(parsed, 'd MMMM yyyy') : value
}

function formatReviewAddress(values: {
  formattedAddress?: string | null
  suburb?: string | null
  state?: string | null
  postcode?: string | null
}) {
  return [
    values.formattedAddress,
    values.suburb,
    [values.state, values.postcode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')
}
